/**
 * Hazard Data Service - Enhanced with State Fire/SES Services (TASK 4)
 * Manages 15 official hazard data sources for C3 mapping overlays
 */

// Import new state fire/SES providers
// import { TasmaniaFireProvider } from './providers/tasmaniaFireProvider.js';

// Hazard severity levels with color coding
export const HAZARD_SEVERITY = {
  LOW: { level: 1, color: '#52c41a', label: 'Low' },       // Green
  MODERATE: { level: 2, color: '#fa8c16', label: 'Moderate' }, // Orange (standardized)
  HIGH: { level: 3, color: '#fa8c16', label: 'High' },        // Orange (standardized)
  EXTREME: { level: 4, color: '#f5222d', label: 'Extreme' }   // Red
};

// Hazard types for filtering
export const HAZARD_TYPES = {
  WEATHER: 'weather',
  FIRE: 'fire', 
  BURN_OFF: 'burn_off',
  EARTHQUAKE: 'earthquake',
  FLOOD: 'flood',
  CYCLONE: 'cyclone',
  TSUNAMI: 'tsunami',
  AIR_QUALITY: 'air_quality',
  TRAFFIC: 'traffic',
  TRANSPORT: 'transport',
  EMERGENCY: 'emergency',
  AMBULANCE: 'ambulance',
  MARINE: 'marine',
  POWER: 'power'
};

// Data source configurations - UPDATED FOR TASK 2: Reduced cache durations
const DATA_SOURCES = {
  OPENWEATHERMAP: {
    name: 'OpenWeatherMap',
    url: 'https://api.openweathermap.org/data/2.5',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: severe weather needs fresh data
    active: true
  },
  AFAC_BUSHFIRE: {
    name: 'AFAC National Bushfire Map',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: fire incidents change rapidly
    active: true
  },
  GEOSCIENCE_EARTHQUAKES: {
    name: 'Global Earthquakes (USGS + Geoscience Australia)',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: earthquake aftershocks
    active: true
  },
  BOM_WARNINGS: {
    name: 'Bureau of Meteorology',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: severe weather warnings
    active: true
  },
  EPA_AIR_QUALITY: {
    name: 'EPA Air Quality',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 30, // 30 minutes - LOWER PRIORITY: slower changing conditions
    active: true
  },

  EMERGENCY_ALERT: {
    name: 'Emergency Alert Australia',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: emergency broadcasts
    active: true
  },
  ABC_EMERGENCY: {
    name: 'ABC Emergency RSS',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: breaking emergency news
    active: true
  },
  BOM_MARINE: {
    name: 'BOM Marine Warnings',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: marine conditions can change quickly
    active: true
  },
  AEMO_POWER: {
    name: 'AEMO Power System',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: power outages affect safety
    active: true
  },
  GOOGLE_TRAFFIC: {
    name: 'Google Traffic',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 5, // 5 minutes - CRITICAL: real-time traffic conditions
    active: true
  },
  // TASK 4: New State Fire/SES Services
  TASMANIA_FIRE: {
    name: 'Tasmania Fire Service',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: fire incidents change rapidly
    active: true
  },
  SA_CFS: {
    name: 'South Australia CFS',
    url: 'local_provider',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: fire/emergency incidents
    active: true
  },
  SA_SES: {
    name: 'South Australia SES',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: SES emergency incidents
    active: true
  },
  QLD_QFES: {
    name: 'Queensland QFES',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - CRITICAL: fire/emergency incidents
    active: true
  },
  // NT and WA Emergency Services
  NT_EMERGENCY: {
    name: 'NT Police, Fire & Emergency Services',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - aligned with other providers
    active: true
  },
  WA_EMERGENCY: {
    name: 'WA Department of Fire & Emergency Services',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - aligned with other providers
    active: true
  },
  VIC_EMERGENCY: {
    name: 'Victoria Emergency Services',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - aligned with other providers
    active: true
  },
  ACT_EMERGENCY: {
    name: 'ACT Emergency Services',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - aligned with other providers
    active: true // ✅ ACTIVATED - ACT ESA provider implemented
  },
  NSW_EMERGENCY: {
    name: 'NSW Emergency Services',
    url: 'cloud_function',
    type: 'json',
    refreshInterval: 15, // 15 minutes - aligned with other providers
    active: true
  }
};

// Cloud Function endpoint - Updated to new Cloud Run URL
const CLOUD_FUNCTION_URL = 'https://fetchhazarddata-dtu2gdip7q-km.a.run.app';

class HazardDataService {
  constructor() {
    // Singleton pattern to prevent multiple instances
    if (HazardDataService.instance) {
      console.log('🔄 Returning existing HazardDataService instance');
      return HazardDataService.instance;
    }
    
    this.cache = new Map();
    this.refreshTimers = new Map();
    this.sources = [];
    this.isInitialized = false;
    this.isInitializing = false;
    this.initializationPromise = null;
    
    // Initialize Tasmania Fire Provider (TASK 4 - Phase 1)
    // this.tasmaniaFireProvider = new TasmaniaFireProvider();
    
    // 🔍 PERFORMANCE-OPTIMIZED: Coordinate validation logging
    this.coordinateErrorCount = 0;
    this.lastErrorLogTime = 0;
    this.ERROR_LOG_RATE_LIMIT = 10000; // Max 1 error log per 5 seconds
    
    // State boundaries for validation (lightweight copy)
    this.STATE_BOUNDS = {
      'VIC': { minLat: -39.4, maxLat: -33.7, minLng: 140.7, maxLng: 150.1 },
      'NSW': { minLat: -37.5, maxLat: -28.1, minLng: 140.9, maxLng: 153.6 },
      'QLD': { minLat: -29.2, maxLat: -9.1, minLng: 137.9, maxLng: 153.6 },
      'WA': { minLat: -35.1, maxLat: -13.4, minLng: 112.9, maxLng: 129.0 },
      'SA': { minLat: -38.1, maxLat: -25.9, minLng: 129.0, maxLng: 141.0 },
      'TAS': { minLat: -43.6, maxLat: -39.2, minLng: 143.8, maxLng: 148.5 },
      'ACT': { minLat: -35.9, maxLat: -35.1, minLng: 148.7, maxLng: 149.4 },
      'NT': { minLat: -26.0, maxLat: -10.9, minLng: 129.0, maxLng: 138.0 }
    };
    
    HazardDataService.instance = this;
    console.log('🆕 Created new HazardDataService instance with Tasmania Fire Provider');
  }

  /**
   * Initialize the service and start data refresh timers
   */
  async initialize() {
    // Prevent multiple simultaneous initializations
    if (this.isInitialized) {
      console.log('🔄 Hazard Data Service already initialized');
      return;
    }
    
    if (this.isInitializing) {
      console.log('⏳ Hazard Data Service initialization in progress, waiting...');
      return await this.initializationPromise;
    }
    
    // Set flag and create promise for other callers to wait on
    this.isInitializing = true;
    this.initializationPromise = this._performInitialization();
    
    try {
      await this.initializationPromise;
    } finally {
      this.isInitializing = false;
      this.initializationPromise = null;
    }
  }

  /**
   * Internal initialization method
   */
  async _performInitialization() {
    console.log('🚨 Initializing Hazard Data Service - Tranche 1');
    
    // CLEAR ANY EXISTING CACHE DATA (remove old mock data)
    this.cache.clear();
    console.log('🧹 Cleared hazard data cache');
    
    // Clear any existing timers
    this.refreshTimers.forEach(timer => clearInterval(timer));
    this.refreshTimers.clear();
    console.log('⏰ Cleared existing refresh timers');
    
    // Force garbage collection of any old references
    if (global.gc) {
      global.gc();
      console.log('🗑️ Forced garbage collection');
    }
    
    // Start refresh timers for each active data source
    Object.entries(DATA_SOURCES).forEach(([key, config]) => {
      if (config.active) {
        this.startRefreshTimer(key, config);
      }
    });
    
    // Initial data fetch for all sources with error handling
    try {
      console.log('🔄 Starting initial data fetch for all sources...');
    await this.refreshAllSources();
      console.log('✅ Initial data fetch completed');
    } catch (error) {
      console.error('🚨 Error during initial data fetch:', error);
      // Don't fail initialization if data fetch fails
    }
    
    // Small delay to ensure all async operations complete
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    this.isInitialized = true;
    console.log('✅ Hazard Data Service initialized successfully');
    console.log('📊 Current cache status:', this.getCacheStatus());
  }

  /**
   * Get current cache status for debugging
   */
  getCacheStatus() {
    const status = {};
    this.cache.forEach((cached, key) => {
      status[key] = {
        hazardCount: cached.data ? cached.data.length : 0,
        lastUpdated: new Date(cached.timestamp).toISOString(),
        ageMinutes: Math.round((Date.now() - cached.timestamp) / 60000)
      };
    });
    return status;
  }

  /**
   * Force refresh all hazard data - CACHE BUSTING for manual refresh
   */
  async forceRefreshAllHazards() {
    console.log('🔄 FORCE REFRESH: Clearing all cache and fetching fresh data');
    
    // Clear local cache
    this.cache.clear();
    console.log('🧹 Cleared local cache');
    
    // Clear OpenWeatherMap provider cache
    try {
      const provider = await import('./providers/openWeatherMapProvider');
      const OpenWeatherMapProvider = provider.default;
      const owmProvider = new OpenWeatherMapProvider();
      owmProvider.clearCache();
      console.log('🧹 Cleared OpenWeatherMap provider cache');
    } catch (error) {
      console.warn('⚠️ Could not clear OpenWeatherMap cache:', error.message);
    }
    
    // Force refresh all sources
    await this.refreshAllSources();
    
    console.log('✅ FORCE REFRESH COMPLETE: All data refreshed from sources');
    return this.getAllHazards(null, null, 0); // Return fresh data with no age limit
  }

  /**
   * Start refresh timer for a data source
   */
  startRefreshTimer(sourceKey, config) {
    const intervalMs = config.refreshInterval * 60 * 1000;
    
    const timer = setInterval(async () => {
      try {
        await this.fetchDataFromSource(sourceKey, config);
      } catch (error) {
        console.error(`🚨 Error refreshing ${config.name}:`, error);
      }
    }, intervalMs);
    
    this.refreshTimers.set(sourceKey, timer);
    console.log(`⏰ Started refresh timer for ${config.name}: ${config.refreshInterval}min`);
  }

  /**
   * Fetch data from all active sources - PROGRESSIVE STREAMING IMPLEMENTATION
   * 🚀 NEW: Stream hazards as each provider completes instead of waiting for all
   */
  async refreshAllSources(onProgressUpdate = null) {
    console.log('🚀 PROGRESSIVE LOADING: Starting streaming refresh of all sources...');
    
    const activeSources = Object.entries(DATA_SOURCES).filter(([, config]) => config.active);
    const totalSources = activeSources.length;
    let completedSources = 0;
    
    console.log(`📊 PROGRESSIVE LOADING: Processing ${totalSources} sources with streaming updates`);
    
    // Create individual promises for each source with immediate callback on completion
    const sourcePromises = activeSources.map(async ([key, config]) => {
      try {
        const startTime = Date.now();
        console.log(`🔄 PROGRESSIVE: Starting ${key} (${config.name})...`);
        
        // Fetch data from this specific source
        await this.fetchDataFromSource(key, config);
        
        const duration = Date.now() - startTime;
        completedSources++;
        
        console.log(`✅ PROGRESSIVE: ${key} completed in ${duration}ms (${completedSources}/${totalSources})`);
        
        // 🚀 CRITICAL: Trigger UI update immediately after each source completes
        if (onProgressUpdate) {
          try {
            onProgressUpdate({
              completedSources,
              totalSources,
              latestSource: key,
              duration,
              // Pass current hazard data for immediate UI update
              currentHazards: this.getAllHazards(null, null, 15, false)
            });
          } catch (callbackError) {
            console.error(`🚨 PROGRESSIVE: Callback error for ${key}:`, callbackError);
          }
        }
        
        return { sourceKey: key, success: true, duration };
        
      } catch (error) {
        completedSources++;
        console.error(`🚨 PROGRESSIVE: ${key} failed:`, error);
        
        // Still trigger callback even on failure to update progress
        if (onProgressUpdate) {
          try {
            onProgressUpdate({
              completedSources,
              totalSources,
              latestSource: key,
              error: error.message,
              currentHazards: this.getAllHazards(null, null, 15, false)
            });
          } catch (callbackError) {
            console.error(`🚨 PROGRESSIVE: Callback error for failed ${key}:`, callbackError);
          }
        }
        
        return { sourceKey: key, success: false, error: error.message };
      }
    });

    // Don't wait for all - let them complete in parallel and stream results
    // But still track overall completion for logging
    const startTime = Date.now();
    
    try {
      const results = await Promise.allSettled(sourcePromises);
      const totalTime = Date.now() - startTime;
      
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)).length;
      
      console.log(`🚀 PROGRESSIVE LOADING COMPLETE:`);
      console.log(`   ⚡ Total time: ${totalTime}ms`);
      console.log(`   ✅ Successful sources: ${successful}/${totalSources}`);
      console.log(`   ❌ Failed sources: ${failed}/${totalSources}`);
      console.log(`   📊 Final hazard count: ${this.getAllHazards(null, null, 15, false).length}`);
      
    } catch (error) {
      console.error('🚨 PROGRESSIVE: Unexpected error in Promise.allSettled:', error);
    }
  }

  /**
   * Fetch data from a specific source
   */
  async fetchDataFromSource(sourceKey, config) {
    console.log(`🔄 fetchDataFromSource called for ${sourceKey} (${config.name})`);
    
    const fetchStartTime = Date.now();
    let refreshFailed = false;
    let errorMessage = null;
    
    try {
      let data;
      
      switch (sourceKey) {
        case 'OPENWEATHERMAP':
          console.log('🌤️ Fetching OpenWeatherMap data...');
          data = await this.fetchOpenWeatherMapData();
          console.log(`🌤️ OpenWeatherMap returned ${data.length} hazards`);
          break;
        case 'AFAC_BUSHFIRE':
          data = await this.fetchAFACBushfireData();
          break;
        case 'GEOSCIENCE_EARTHQUAKES':
          data = await this.fetchUnifiedEarthquakeData();
          break;
        case 'BOM_WARNINGS':
          data = await this.fetchBOMWarningsData();
          break;
        case 'EPA_AIR_QUALITY':
          data = await this.fetchEPAAirQualityData();
          break;

        case 'EMERGENCY_ALERT':
          data = await this.fetchEmergencyAlertData();
          break;
        case 'ABC_EMERGENCY':
          data = await this.fetchABCEmergencyData();
          break;
        case 'BOM_MARINE':
          data = await this.fetchBOMMarineData();
          break;
        case 'AEMO_POWER':
          data = await this.fetchAEMOPowerData();
          break;
        case 'GOOGLE_TRAFFIC':
          data = await this.fetchGoogleTrafficData();
          break;
        // TASK 4: New State Fire/SES Service providers
        case 'TASMANIA_FIRE':
          console.log('🔥 Fetching Tasmania Fire Service data...');
          data = await this.fetchFromCloudFunction('tasmania_fire');
          console.log(`🔥 Tasmania Fire returned ${data.length} hazards`);
          break;
        case 'NT_EMERGENCY':
          data = await this.fetchFromCloudFunction('nt_emergency');
          break;
        case 'WA_EMERGENCY':
          data = await this.fetchFromCloudFunction('wa_emergency');
          break;
        case 'QLD_QFES':
          data = await this.fetchFromCloudFunction('qld_qfes');
          break;
        case 'SA_CFS':
          console.log('🔥 Fetching SA CFS data...');
          data = await this.fetchFromCloudFunction('sa_cfs');
          console.log(`🔥 SA CFS returned ${data.length} hazards`);
          break;
        case 'SA_SES':
          console.log('🚨 Fetching SA SES data...');
          data = await this.fetchFromCloudFunction('sa_ses');
          console.log(`🚨 SA SES returned ${data.length} hazards`);
          break;
        case 'VIC_EMERGENCY':
          console.log('🚨 Fetching VIC Emergency data...');
          data = await this.fetchFromCloudFunction('vic_emergency');
          console.log(`🚨 VIC Emergency returned ${data.length} hazards`);
          break;
        case 'ACT_EMERGENCY':
          console.log('🚨 Fetching ACT Emergency data...');
          data = await this.fetchFromCloudFunction('act_emergency');
          console.log(`🚨 ACT Emergency returned ${data.length} hazards`);
          break;
        case 'NSW_EMERGENCY':
          console.log('🚨 Fetching NSW Emergency data...');
          data = await this.fetchFromCloudFunction('nsw_emergency');
          console.log(`🚨 NSW Emergency returned ${data.length} hazards`);
          break;
        default:
          console.warn(`🚨 Unknown data source: ${sourceKey}`);
          return;
      }
      
      // TASK 3: Add refresh status tracking to each hazard
      const enhancedData = data.map(hazard => ({
        ...hazard,
        lastUpdated: fetchStartTime,
        refreshFailed: false,
        fetchDuration: Date.now() - fetchStartTime
      }));
      
      // Cache the data with enhanced tracking
      console.log(`💾 Caching ${sourceKey} with ${enhancedData.length} hazards`);
      this.cache.set(sourceKey, {
        data: enhancedData,
        timestamp: Date.now(),
        source: config.name,
        lastRefreshAttempt: fetchStartTime,
        refreshFailed: false,
        errorMessage: null
      });
      
      // Verify cache was set
      const cached = this.cache.get(sourceKey);
      console.log(`✅ Cache verification for ${sourceKey}: ${cached ? 'SUCCESS' : 'FAILED'}`);
      if (cached) {
        console.log(`✅ Cached data length: ${cached.data.length}`);
      }
      
      console.log(`✅ Updated ${config.name}: ${enhancedData.length} hazards`);
      
    } catch (error) {
      console.error(`🚨 Error fetching ${config.name}:`, error);
      refreshFailed = true;
      errorMessage = error.message;
      
      // TASK 3: On failure, keep existing cached data but mark as refresh failed
      const existingCache = this.cache.get(sourceKey);
      if (existingCache && existingCache.data) {
        console.log(`⚠️ Keeping cached data for ${sourceKey} but marking refresh as failed`);
        
        // Mark existing hazards as having failed refresh
        const staleCachedData = existingCache.data.map(hazard => ({
          ...hazard,
          refreshFailed: true,
          lastRefreshAttempt: fetchStartTime,
          refreshError: errorMessage
        }));
        
        this.cache.set(sourceKey, {
          ...existingCache,
          data: staleCachedData,
          lastRefreshAttempt: fetchStartTime,
          refreshFailed: true,
          errorMessage: errorMessage
        });
      }
      
      throw error;
    }
  }

  /**
   * Get all hazard data for map display
   * ENHANCED: Support for low severity toggle with detailed logging
   */
  getAllHazards(bounds = null, types = null, maxAge = 15, showLowSeverity = false) {
    const hazards = [];
    const maxAgeMs = maxAge * 60 * 1000;
    const now = Date.now();
    
    // Enhanced logging for toggle feature validation
    console.log('🔍 HAZARD SERVICE: getAllHazards() called');
    console.log('🔍 Show Low Severity:', showLowSeverity);
    console.log('🔍 Cache size:', this.cache.size);
    console.log('🔍 Cache keys:', Array.from(this.cache.keys()));
    
    // Counters for validation logging
    let totalHazards = 0;
    let lowSeverityCount = 0;
    let moderatePlusCount = 0;
    let filteredByBounds = 0;
    let filteredByType = 0;
    let filteredByAge = 0;
    
    this.cache.forEach((cached, sourceKey) => {
      console.log(`🔍 Checking source: ${sourceKey} (${cached.source})`);
      console.log(`🔍 Data array length: ${cached.data ? cached.data.length : 'null'}`);
      console.log(`🔍 Data age: ${Math.round((now - cached.timestamp) / 60000)} minutes`);
      
      if (cached.data && cached.data.length > 0) {
        console.log(`🔍 First item from ${sourceKey}:`, {
          id: cached.data[0].id,
          title: cached.data[0].title,
          location: cached.data[0].metadata?.location,
          isMock: (cached.data[0].id && cached.data[0].id.includes('mock')) || (cached.data[0].metadata?.location && cached.data[0].metadata.location.includes('Mock'))
        });
      }
      
      // Check if data is still fresh
      if (now - cached.timestamp > maxAgeMs) {
        console.warn(`⚠️ Stale data for ${cached.source}, age: ${Math.round((now - cached.timestamp) / 60000)}min`);
        filteredByAge += cached.data?.length || 0;
        return;
      }
      
      if (cached.data && Array.isArray(cached.data)) {
        cached.data.forEach(hazard => {
          totalHazards++;
          
          // 🔥 CRITICAL FIX: Text classification MUST happen BEFORE type filtering
          // Otherwise burn offs get filtered out as 'fire' before they can be reclassified
          const originalType = hazard.type;
          const reclassifiedType = this._classifyHazardByText(hazard);
          
          // 🚑 FIXED: Filter by type using RECLASSIFIED type (after text classification)
          if (types && !types.includes(reclassifiedType)) {
            filteredByType++;
            console.log(`🏷️ FILTERED OUT: Hazard "${hazard.title}" type ${reclassifiedType} not in ${types.join(',')}`);
            return;
          }
          
          // Filter by bounds if provided
          if (bounds && !this.isHazardInBounds(hazard, bounds)) {
            filteredByBounds++;
            console.log(`🗺️ FILTERED OUT: Hazard "${hazard.title}" outside bounds`);
            return;
          }
          
          // ENHANCED: Conditional severity filtering based on toggle (AFTER text classification)
          const severityLevel = hazard.severity?.level || 0;
          const isLowSeverity = severityLevel < HAZARD_SEVERITY.MODERATE.level;
          
          if (isLowSeverity) {
            lowSeverityCount++;
            if (!showLowSeverity) {
              console.log(`🔽 FILTERED OUT: LOW severity hazard "${hazard.title}" from ${cached.source} (level: ${severityLevel})`);
              return;
            } else {
              console.log(`🔵 INCLUDING: LOW severity hazard "${hazard.title}" from ${cached.source} (level: ${severityLevel}) - Toggle ON`);
            }
          } else {
            moderatePlusCount++;
            console.log(`🟡 INCLUDING: MODERATE+ severity hazard "${hazard.title}" from ${cached.source} (level: ${severityLevel})`);
          }
          
          hazards.push({
            ...hazard,
            type: reclassifiedType, // Use reclassified type for filtering
            originalType: originalType, // Preserve original for safety
            source: cached.source,
            lastUpdated: cached.timestamp,
            // Add severity classification for UI differentiation
            isLowSeverity: isLowSeverity,
            // Add classification metadata for monitoring
            wasReclassified: reclassifiedType !== originalType
          });
          
          // 🔍 PERFORMANCE-OPTIMIZED: Validate coordinates after processing
          this._validateHazardCoordinates(hazards[hazards.length - 1]);
        });
      }
    });
    
    // VALIDATION LOGGING: Detailed breakdown for toggle verification
    console.log('\n📊 HAZARD FILTERING SUMMARY:');
    console.log(`📊 Total hazards from all sources: ${totalHazards}`);
    console.log(`📊 Low severity hazards (level < 2): ${lowSeverityCount}`);
    console.log(`📊 Moderate+ severity hazards (level >= 2): ${moderatePlusCount}`);
    console.log(`📊 Filtered by age (stale): ${filteredByAge}`);
    console.log(`📊 Filtered by bounds: ${filteredByBounds}`);
    console.log(`📊 Filtered by type: ${filteredByType}`);
    console.log(`📊 Show low severity toggle: ${showLowSeverity ? 'ON' : 'OFF'}`);
    console.log(`📊 FINAL HAZARDS ON MAP: ${hazards.length}`);
    
    if (showLowSeverity) {
      console.log(`📊 Low severity included: ${hazards.filter(h => h.isLowSeverity).length}`);
      console.log(`📊 Moderate+ included: ${hazards.filter(h => !h.isLowSeverity).length}`);
    } else {
      console.log(`📊 Low severity filtered out: ${lowSeverityCount}`);
      console.log(`📊 Only moderate+ shown: ${hazards.length}`);
    }
    
    if (hazards.length > 0) {
      console.log('\n🔍 HAZARDS ON MAP:');
      hazards.forEach((hazard, i) => {
        const severityIcon = hazard.isLowSeverity ? '🔵' : '🟡';
        console.log(`🔍 ${i+1}. ${severityIcon} ${hazard.title} from ${hazard.source} (severity: ${hazard.severity?.level}, ID: ${hazard.id})`);
      });
    }
    
    // 🔥 HYPER-CAUTIOUS: Log reclassification summary for monitoring
    const reclassifiedCount = hazards.filter(h => h.wasReclassified).length;
    if (reclassifiedCount > 0) {
      console.log(`🔥 TEXT CLASSIFICATION: ${reclassifiedCount} hazards reclassified`);
      hazards.filter(h => h.wasReclassified).forEach(h => {
        console.log(`   "${h.title}" ${h.originalType} → ${h.type}`);
      });
    }

    return hazards.sort((a, b) => b.severity.level - a.severity.level);
  }

  /**
   * 🔥 HYPER-CAUTIOUS: Conservative text-based burn off classification
   * Uses ONLY the exact 4 keywords that work in ACT/WA providers
   */
  _classifyHazardByText(hazard) {
    try {
      console.log(`\n🔍 DIAGNOSTIC: TEXT CLASSIFICATION CALLED FOR "${hazard.title}"`);
      
      // SAFETY: If no text content, return original type unchanged
      if (!hazard.title && !hazard.description) {
        console.log(`❌ NO TEXT CONTENT - Title: "${hazard.title}", Description: "${hazard.description}"`);
        return hazard.type;
      }

      // Combine all text content for analysis
      const textContent = [
        hazard.title || '',
        hazard.description || '',
        hazard.status || ''
      ].join(' ').toLowerCase().trim();

      console.log(`🔍 RAW TEXT CONTENT (first 200 chars): "${textContent.substring(0, 200)}..."`);
      console.log(`🔍 TEXT LENGTH: ${textContent.length} characters`);

      // SAFETY: If no meaningful text, return original type
      if (!textContent || textContent.length < 3) {
        console.log(`❌ INSUFFICIENT TEXT CONTENT - Length: ${textContent.length}`);
        return hazard.type;
      }

      // HYPER-CAUTIOUS: Use ONLY the exact 4 keywords from working ACT/WA implementations
      const burnOffKeywords = [
        'burn off',
        'burn-off',
        'planned burn',
        'prescribed burn'
      ];

      console.log(`🔍 CHECKING KEYWORDS: ${burnOffKeywords.join(', ')}`);

      // SAFETY: Emergency exclusions - never classify as burn off if these are present
      const emergencyExclusions = [
        'evacuation',
        'emergency warning',
        'watch and act',
        'out of control',
        'threat to life',
        'immediate threat',
        'emergency alert',
        'urgent'
      ];

      // SAFETY CHECK: Exclude if contains emergency indicators
      const hasEmergencyIndicators = emergencyExclusions.some(exclusion => {
        const found = textContent.includes(exclusion);
        if (found) {
          console.log(`🚨 EMERGENCY EXCLUSION TRIGGERED: "${exclusion}"`);
        }
        return found;
      });

      if (hasEmergencyIndicators) {
        console.log(`❌ EMERGENCY EXCLUSION - NOT RECLASSIFYING`);
        return hazard.type;
      }

      // CONSERVATIVE: Only reclassify if burn off keywords present
      let matchedKeyword = null;
      const hasBurnOffIndicators = burnOffKeywords.some(keyword => {
        const found = textContent.includes(keyword);
        console.log(`🔍 CHECKING "${keyword}": ${found ? '✅ FOUND' : '❌ NOT FOUND'}`);
        if (found) {
          matchedKeyword = keyword;
        }
        return found;
      });

      if (hasBurnOffIndicators) {
        console.log(`🔥 MATCH FOUND! Keyword: "${matchedKeyword}"`);
        console.log(`🔥 RECLASSIFYING: "${hazard.title}" ${hazard.type} → burn_off`);
        return 'burn_off';
      }

      console.log(`❌ NO BURN OFF KEYWORDS FOUND - KEEPING ORIGINAL TYPE: ${hazard.type}`);
      return hazard.type;

    } catch (error) {
      // SAFETY: On any error, return original type unchanged
      console.error('🚨 TEXT CLASSIFICATION ERROR:', error);
      console.error('🚨 Hazard that caused error:', hazard.title);
      return hazard.type;
    }
  }

  /**
   * Check if hazard is within map bounds
   */
  isHazardInBounds(hazard, bounds) {
    if (!hazard.latitude || !hazard.longitude) return false;
    
    return (
      hazard.latitude >= bounds.south &&
      hazard.latitude <= bounds.north &&
      hazard.longitude >= bounds.west &&
      hazard.longitude <= bounds.east
    );
  }

  // Data source implementations - now using Cloud Functions proxy
  async fetchOpenWeatherMapData() {
    // Keep OpenWeatherMap working directly since it's already functional
    const provider = await import('./providers/openWeatherMapProvider');
    const OpenWeatherMapProvider = provider.default;
    const owmProvider = new OpenWeatherMapProvider();
    return await owmProvider.fetchWeatherHazards();
  }

  async fetchAFACBushfireData() {
    return await this.fetchFromCloudFunction('afac');
  }

  async fetchUnifiedEarthquakeData() {
    // Fetches from unified earthquake collection (USGS Global + Geoscience Australia)
    // with intelligent deduplication and conflict resolution
    return await this.fetchFromCloudFunction('ga');
  }

  // Legacy alias for backward compatibility
  async fetchGeoscienceEarthquakeData() {
    return await this.fetchUnifiedEarthquakeData();
  }

  async fetchBOMWarningsData() {
    return await this.fetchFromCloudFunction('bom');
  }

  async fetchEPAAirQualityData() {
    return await this.fetchFromCloudFunction('epa');
  }



  async fetchEmergencyAlertData() {
    return await this.fetchFromCloudFunction('emergency_alert');
  }

  async fetchABCEmergencyData() {
    return await this.fetchFromCloudFunction('abc_emergency');
  }

  async fetchBOMMarineData() {
    return await this.fetchFromCloudFunction('bom_marine');
  }

  async fetchAEMOPowerData() {
    return await this.fetchFromCloudFunction('aemo_power');
  }

  async fetchGoogleTrafficData() {
    return await this.fetchFromCloudFunction('google_traffic');
  }

  /**
   * Fetch data from Cloud Function proxy - FIXED to use individual endpoints
   */
  async fetchFromCloudFunction(provider) {
    try {
      // Use individual provider endpoints instead of main aggregated endpoint
      const individualEndpointURL = `https://australia-southeast2-crisiscompass-prod.cloudfunctions.net/${provider}`;
      
      console.log(`🔄 Calling individual endpoint: ${individualEndpointURL}`);
      
      const response = await fetch(individualEndpointURL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Individual endpoint error: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log(`✅ Cloud Function ${provider}: ${result.count} hazards`);
        return result.data || [];
      } else {
        throw new Error(result.error || 'Individual endpoint returned error');
      }

    } catch (error) {
      console.error(`🚨 Individual endpoint ${provider} error:`, error);
      return []; // Return empty array on error
    }
  }

  /**
   * Get hazard type for provider filtering
   */
  getHazardTypeForProvider(provider) {
    const typeMap = {
      afac: HAZARD_TYPES.FIRE,
      ga: HAZARD_TYPES.EARTHQUAKE,
      bom: HAZARD_TYPES.WEATHER,
      epa: HAZARD_TYPES.AIR_QUALITY,

      emergency_alert: HAZARD_TYPES.EMERGENCY,
      abc_emergency: HAZARD_TYPES.EMERGENCY,
      bom_marine: HAZARD_TYPES.MARINE,
      aemo_power: HAZARD_TYPES.POWER,
      google_traffic: HAZARD_TYPES.TRAFFIC,
      // TASK 4: New State Fire/SES Services
      tasmania_fire: HAZARD_TYPES.FIRE,
      sa_cfs: HAZARD_TYPES.FIRE,
      qld_qfes: HAZARD_TYPES.FIRE,
      // NT & WA Emergency Services - WA updated to support both fire and burn_off
      nt_emergency: HAZARD_TYPES.EMERGENCY,
      wa_emergency: HAZARD_TYPES.FIRE, // WA provider returns both 'fire' and 'burn_off' types
      nsw_emergency: HAZARD_TYPES.FIRE,
      act_emergency: HAZARD_TYPES.EMERGENCY
    };
    return typeMap[provider] || null;
  }

  /**
   * Test Cloud Function connectivity
   */
  async testCloudFunctionConnectivity() {
    try {
      console.log('🧪 Testing Cloud Function connectivity...');
      
      const response = await fetch(CLOUD_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          types: [HAZARD_TYPES.WEATHER],
          forceRefresh: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Cloud Function connectivity test passed');
        console.log(`📊 Sources available: ${result.sources.join(', ')}`);
        console.log(`📈 Total hazards: ${result.data.length}`);
        return {
          success: true,
          sources: result.sources,
          hazardCount: result.data.length,
          timestamp: result.timestamp
        };
      } else {
        throw new Error(result.error || 'Cloud Function returned error');
      }

    } catch (error) {
      console.error('🚨 Cloud Function connectivity test failed:', error);
      return {
        success: false,
        error: error.message,
        timestamp: Date.now()
      };
    }
  }

  /**
   * Get detailed status of all hazard data sources
   */
  getDataSourceStatus() {
    const status = {
      cloudFunction: {
        url: CLOUD_FUNCTION_URL,
        deployed: true, // Updated to true since function is now deployed
        lastTested: null
      },
      providers: {
        openweathermap: { 
          status: 'active', 
          type: 'direct', 
          lastFetch: this.cache.get('OPENWEATHERMAP')?.timestamp || null 
        },
        bom: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'Bureau of Meteorology - Weather warnings, floods, cyclones'
        },
        afac: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'AFAC Emergency Services - Bushfire incidents'
        },
        geoscience: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'Global Earthquakes - USGS + Geoscience Australia unified feed'
        },
        epa: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'EPA Air Quality - Multi-state air monitoring'
        },

        emergency_alert: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'Emergency Alert Australia - National emergency broadcasts'
        },
        abc_emergency: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'ABC Emergency RSS - Breaking news and local incidents'
        },
        bom_marine: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'BOM Marine Warnings - Coastal hazards and marine weather'
        },
        aemo_power: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'AEMO Power System - Power outages and grid failures'
        },
        google_traffic: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'Google Traffic - Real-time traffic incidents and road closures'
        },
        // TASK 4: New State Fire/SES Services
        tasmania_fire: { 
          status: 'active', 
          type: 'local_provider',
          description: 'Tasmania Fire Service - Fire incidents and emergency alerts',
          lastFetch: this.cache.get('TASMANIA_FIRE')?.timestamp || null
        },
        sa_cfs: { 
          status: 'active', 
          type: 'local_provider',
          description: 'South Australia CFS - Fire and emergency incidents',
          lastFetch: this.cache.get('SA_CFS')?.timestamp || null
        },
        sa_ses: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'South Australia SES - Emergency rescue, severe weather, flooding',
          lastFetch: this.cache.get('SA_SES')?.timestamp || null
        },
        qld_qfes: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'Queensland QFES - Fire and emergency incidents',
          lastFetch: this.cache.get('QLD_QFES')?.timestamp || null
        },
        // NT & WA Emergency Services
        nt_emergency: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'NT Police, Fire & Emergency Services - Emergency incidents and alerts',
          lastFetch: this.cache.get('NT_EMERGENCY')?.timestamp || null
        },
        wa_emergency: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'WA Department of Fire & Emergency Services - Fire and emergency incidents',
          lastFetch: this.cache.get('WA_EMERGENCY')?.timestamp || null
        },
        nsw_emergency: { 
          status: 'active', 
          type: 'cloud_function',
          description: 'NSW Rural Fire Service - Major fire incidents and emergency updates',
          lastFetch: this.cache.get('NSW_EMERGENCY')?.timestamp || null
        }
      },
      cache: {
        totalHazards: this.getAllHazards().length,
        lastRefresh: Math.max(...Array.from(this.cache.values()).map(item => item.timestamp || 0))
      }
    };

    return status;
  }

  /**
   * Cleanup - stop all timers
   */
  destroy() {
    this.refreshTimers.forEach(timer => clearInterval(timer));
    this.refreshTimers.clear();
    this.cache.clear();
    this.isInitialized = false;
    this.isInitializing = false;
    this.initializationPromise = null;
    
    // Clear singleton instance
    HazardDataService.instance = null;
    console.log('🧹 Hazard Data Service destroyed');
  }

  /**
   * Static method to get or create instance
   */
  static getInstance() {
    if (!HazardDataService.instance) {
      HazardDataService.instance = new HazardDataService();
    }
    return HazardDataService.instance;
  }

  /**
   * Static method to clear singleton instance
   */
  static clearInstance() {
    if (HazardDataService.instance) {
      HazardDataService.instance.destroy();
    }
    HazardDataService.instance = null;
  }

  /**
   * 🔍 PERFORMANCE-OPTIMIZED: Validate hazard coordinates and log anomalies
   * Only logs errors with rate limiting to prevent performance impact
   */
  _validateHazardCoordinates(hazard) {
    const { latitude, longitude, metadata, title, id } = hazard;
    const state = metadata?.state;
    
    // PERFORMANCE: Quick validation checks first
    if (!latitude || !longitude || !state) return true;
    
    let isValid = true;
    let errorType = null;
    
    // 1. Check for suspicious Darwin default coordinates (fastest check)
    if (latitude === -12.4634 && longitude === 130.8456 && state !== 'NT') {
      isValid = false;
      errorType = 'darwin_default_coordinates';
    }
    
    // 2. State boundary validation (only if we have bounds for this state)
    else if (this.STATE_BOUNDS[state]) {
      const bounds = this.STATE_BOUNDS[state];
      if (latitude < bounds.minLat || latitude > bounds.maxLat || 
          longitude < bounds.minLng || longitude > bounds.maxLng) {
        isValid = false;
        errorType = 'coordinate_state_mismatch';
      }
    }
    
    // 3. Check for common coordinate swaps (VIC/NSW mix-ups)
    else if (state === 'VIC' && latitude === -33.8688 && longitude === 151.2093) {
      isValid = false;
      errorType = 'vic_incident_sydney_coords';
    }
    else if (state === 'NSW' && latitude === -37.8136 && longitude === 144.9631) {
      isValid = false;
      errorType = 'nsw_incident_melbourne_coords';
    }
    
    // PERFORMANCE: Only log errors with rate limiting
    if (!isValid) {
      this._logCoordinateError(hazard, errorType);
    }
    
    // PERFORMANCE: Always log Victorian incidents (for monitoring) but only in development
    else if (state === 'VIC' && process.env.NODE_ENV === 'development') {
      console.log(`🔍 VIC_COORD_OK: "${title}" | ${latitude}, ${longitude}`);
    }
    
    return isValid;
  }

  /**
   * 🔍 PERFORMANCE-OPTIMIZED: Log coordinate errors with rate limiting and persistence
   */
  _logCoordinateError(hazard, errorType) {
    const now = Date.now();
    
    // PERFORMANCE: Rate limit error logging to prevent spam
    if (now - this.lastErrorLogTime < this.ERROR_LOG_RATE_LIMIT) {
      this.coordinateErrorCount++;
      return; // Skip logging but count the error
    }
    
    this.lastErrorLogTime = now;
    
    // Log to console with clear formatting
    console.error(`🚨 COORDINATE_ERROR: ${errorType.toUpperCase()}`);
    console.error(`🚨 Incident: "${hazard.title}" (ID: ${hazard.id})`);
    console.error(`🚨 Coordinates: ${hazard.latitude}, ${hazard.longitude}`);
    console.error(`🚨 Expected State: ${hazard.metadata?.state}`);
    console.error(`🚨 Source: ${hazard.metadata?.source}`);
    
    if (this.coordinateErrorCount > 0) {
      console.error(`🚨 Additional errors suppressed: ${this.coordinateErrorCount}`);
      this.coordinateErrorCount = 0;
    }
    
    // PERFORMANCE: Async persistent logging to avoid blocking
    setTimeout(() => this._persistCoordinateError(hazard, errorType), 0);
  }

  /**
   * 🔍 PERFORMANCE-OPTIMIZED: Persist coordinate errors to localStorage (async)
   */
  _persistCoordinateError(hazard, errorType) {
    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        errorType,
        hazardId: hazard.id,
        title: hazard.title,
        coordinates: { lat: hazard.latitude, lng: hazard.longitude },
        state: hazard.metadata?.state,
        source: hazard.metadata?.source,
        userAgent: navigator.userAgent.substring(0, 100), // Truncate for storage
        url: window.location.pathname // Just pathname, not full URL
      };
      
      // PERFORMANCE: Limit stored errors to prevent localStorage bloat
      const existingErrors = JSON.parse(localStorage.getItem('coordinateErrors') || '[]');
      existingErrors.push(errorLog);
      
      // Keep only last 20 errors (reduced from 50 for performance)
      if (existingErrors.length > 20) {
        existingErrors.splice(0, existingErrors.length - 20);
      }
      
      localStorage.setItem('coordinateErrors', JSON.stringify(existingErrors));
      
    } catch (error) {
      // PERFORMANCE: Silent fail on localStorage errors to avoid blocking
      console.warn('Failed to persist coordinate error:', error.message);
    }
  }

  /**
   * 🔍 DEBUGGING UTILITY: Retrieve coordinate error logs
   * Usage in console: HazardDataService.getCoordinateErrors()
   */
  static getCoordinateErrors() {
    try {
      const errors = JSON.parse(localStorage.getItem('coordinateErrors') || '[]');
      console.table(errors);
      return errors;
    } catch (error) {
      console.error('Failed to retrieve coordinate errors:', error);
      return [];
    }
  }

  /**
   * 🔍 DEBUGGING UTILITY: Clear coordinate error logs
   * Usage in console: HazardDataService.clearCoordinateErrors()
   */
  static clearCoordinateErrors() {
    try {
      localStorage.removeItem('coordinateErrors');
      console.log('✅ Coordinate error logs cleared');
    } catch (error) {
      console.error('Failed to clear coordinate errors:', error);
    }
  }
}

// Export the class
export { HazardDataService };

// Export singleton instance for backward compatibility
export const hazardDataService = HazardDataService.getInstance();
export default HazardDataService;